import { Plan } from "src/utils/redux/slices/plans";
import { send } from "src/utils/fetch";
import { shift_days, toISO } from "src/utils/datetime";
import { getCountryCode, getLocale } from "src/utils/meristemContext";
import { genderConstants, sexConstants } from "src/utils/sexAndGender";
import { saveSurveyAnswers } from "src/utils/redux/slices/userData";
import { MenopauseSymptoms } from "@components/hrt/utils/menopauseSymptoms";
import { authenticateUser } from "./deepLinksUtils";
import { HealthPlan, MED_SKU, NON_MED_PLAN } from "src/utils/medEligibility";
import { SurveyAnswersState } from "src/utils/redux/slices/surveyAnswers";

type SaveSurveyAnswerParams = Parameters<typeof saveSurveyAnswers>;

// BMI profile configurations
const HIGH_BMI_PROFILE = {
  heightFeet: 5,
  heightInch: 5,
  weight: 300,
  heightCm: 165.1,
  weightKg: 136.08,
  currentBMI: 33.3,
  idealWeight: 170,
  idealWeightKg: 77.1,
  highestWeight: 325,
};

const LOW_BMI_PROFILE = {
  heightFeet: 5,
  heightInch: 5,
  weight: 130,
  heightCm: 165.1,
  weightKg: 58.97,
  currentBMI: 21.6,
  idealWeight: 145,
  idealWeightKg: 65.77,
  highestWeight: 140,
};

const PD_QUALIFIED = {
  pdLifting: ["none"] as ["none"],
  pdGastrointestinalDiscomfort: ["00"] as ["00"],
  pdGastrointestinalIssues: ["none"] as ["none"],
  pdSideEffectConcern: ["notConcerned"] as ["notConcerned"],
  pdNauseaImpact: ["no"] as ["no"],
};

const PD_NOT_QUALIFIED = {
  pdLifting: ["02_to_03"] as ["02_to_03"],
  pdGastrointestinalDiscomfort: ["00"] as ["00"],
  pdGastrointestinalIssues: ["none"] as ["none"],
  pdSideEffectConcern: ["notConcerned"] as ["notConcerned"],
  pdNauseaImpact: ["no"] as ["no"],
};

// Previous medication history
const HAS_PRIOR_MEDS = {
  previousMedications: ["yes"],
  whichMedication: ["wegovy", "ozempic"],
};

const NO_PRIOR_MEDS = {
  previousMedications: ["no"],
};

export const getBasePayloadForMedUser = (
  email: string
): {
  surveyAnswers: SaveSurveyAnswerParams[0];
  paymentSurveyAnswers: SaveSurveyAnswerParams[0];
  params: SaveSurveyAnswerParams[1];
} => ({
  surveyAnswers: {
    heightFeet: 5,
    heightInch: 5,
    weight: 300,
    heightCm: 165.1,
    weightKg: 90.70294784580499,
    currentBMI: 33.27810650887574,
    idealWeight: 170,
    idealWeightKg: 77.09750566893423,
    highestWeight: 325,
    targetBMI: 22.4,
    sex: [sexConstants.female],
    gender: [genderConstants.female],
    pregnant: ["no"],
    ageRange: ["30s"],
    medOptIn: ["yes"],
    medicare: ["no"],
    currentHealthRiskMedical: ["none", { other: "abcd" }],
    previousMedications: ["yes"],
    whichMedication: ["wegovy", "ozempic"],
    zipcode: "98118",
    email,
  },
  paymentSurveyAnswers: {
    ...PD_QUALIFIED,
    pdInjectingComfort: ["extremelyComfortable"],
  },
  params: {
    surveyNameSpace: "personaSurveyUS",
    overwrite: true,
    extraProps: {
      name: "",
      clientTimestamp: "2021-09-06T19:27:21.855Z[Europe/Bucharest]",
      timezone: "Europe/Bucharest",
      isEUCitizen: false,
      mainSurveyCompleteDateISO: "2021-09-06T16:27:21.855Z",
      email,
      latestMixPanelAlias: email,
      language: "en",
      lastUpdated: 1630945641855,
    },
  },
});

const getPayloadForLowBmiMedUser = (
  email: string
): {
  surveyAnswers: SaveSurveyAnswerParams[0];
  paymentSurveyAnswers: SaveSurveyAnswerParams[0];
  params: SaveSurveyAnswerParams[1];
} => {
  const medPayload = getBasePayloadForMedUser(email);
  medPayload.surveyAnswers = {
    ...medPayload.surveyAnswers,
    weight: 140,
    weightKg: 63.5029,
    currentBMI: 23.3,
    previousMedications: ["no"],
  };
  return medPayload;
};

const getPayloadForLiraglutideUser = (
  email: string
): {
  surveyAnswers: SaveSurveyAnswerParams[0];
  paymentSurveyAnswers: SaveSurveyAnswerParams[0];
  params: SaveSurveyAnswerParams[1];
} => {
  const basePayload = getBasePayloadForMedUser(email);
  return {
    ...basePayload,
    paymentSurveyAnswers: {
      ...basePayload.paymentSurveyAnswers,
      ...PD_NOT_QUALIFIED,
    },
  };
};

export const getPayloadForMedUser = (
  email: string,
  planType?: string
): {
  surveyAnswers: SaveSurveyAnswerParams[0];
  paymentSurveyAnswers: SaveSurveyAnswerParams[0];
  params: SaveSurveyAnswerParams[1];
} => {
  if (planType === "compoundedMicrodoseProactiveHealthBiomarker") {
    return getPayloadForLowBmiMedUser(email);
  }

  if (planType === "genericLiraglutide") {
    return getPayloadForLiraglutideUser(email);
  }

  return getBasePayloadForMedUser(email);
};

export const getPayloadForGlp1CompanionUser = (
  email: string
): {
  surveyAnswers: SaveSurveyAnswerParams[0];
  paymentSurveyAnswers: SaveSurveyAnswerParams[0];
  params: SaveSurveyAnswerParams[1];
} => ({
  surveyAnswers: {
    basicHealthGoal: ["fit"],
    heightFeet: 5,
    heightInch: 5,
    weight: 300,
    heightCm: 165.1,
    weightKg: 90.70294784580499,
    currentBMI: 33.27810650887574,
    idealWeight: 170,
    idealWeightKg: 77.09750566893423,
    targetBMI: 22.4,
    sex: [sexConstants.female],
    gender: [genderConstants.female],
    pregnant: ["no"],
    ageRange: ["60s"],
    noomMoodBundle: ["yes"],
    glp1: ["yes"],
    glpCompanionAdded: true,
    email,
  },
  paymentSurveyAnswers: {
    ...PD_QUALIFIED,
    pdInjectingComfort: ["extremelyComfortable"],
  },
  params: {
    surveyNameSpace: "personaSurveyUS",
    overwrite: true,
    extraProps: {
      name: "",
      clientTimestamp: "2021-09-06T19:27:21.855Z[Europe/Bucharest]",
      timezone: "Europe/Bucharest",
      isEUCitizen: false,
      mainSurveyCompleteDateISO: "2021-09-06T16:27:21.855Z",
      email,
      latestMixPanelAlias: email,
      language: "en",
      lastUpdated: 1630945641855,
    },
  },
});

export const getPayloadForMhtUser = (
  email: string
): {
  personaSurveyAnswers: SaveSurveyAnswerParams[0];
  paymentSurveyAnswers: SaveSurveyAnswerParams[0];
} => ({
  personaSurveyAnswers: {
    basicHealthGoal: ["fit"],
    heightFeet: 5,
    heightInch: 5,
    weight: 200,
    heightCm: 165.1,
    weightKg: 90.70294784580499,
    currentBMI: 33.27810650887574,
    idealWeight: 170,
    idealWeightKg: 77.09750566893423,
    targetBMI: 22.4,
    sex: [sexConstants.female],
    gender: [genderConstants.female],
    pregnant: ["no"],
    ageRange: ["50s"],
    ageYears: 50,
    zipcode: "10001",
    currentHealthRiskHRT: ["none"],
    email,
  },
  paymentSurveyAnswers: {
    menstrualCycle: ["no"],
    lastPeriod: ["from1To2Years"],
    periodEndReason: ["menopause"],
    menopauseSymptoms: [
      MenopauseSymptoms.HOT_FLASHES,
      MenopauseSymptoms.NIGHT_SWEATS,
    ],
  },
});

export const getPayloadForHRTCheckout = ({
  planType,
}: {
  planType: "cream" | "patch";
}): {
  hrtSurveyAnswers: SaveSurveyAnswerParams[0];
} => ({
  hrtSurveyAnswers: {
    topMenopauseSymptom: [MenopauseSymptoms.HOT_FLASHES],
    takingHRT: ["yes"],
    currentHRTTreatment:
      planType === "cream" ? ["hormonalCreamOrGel"] : ["estrogenPatch"],
    currentHRTPreference: ["yes"],
  },
});

export const getPayloadForCreateUser = (
  email: string,
  payloadOverrides?: Record<string, any>
) => ({
  name: "",
  clientTimestamp: "2021-09-06T19:27:21.855Z[Europe/Bucharest]",
  timezone: "Europe/Bucharest",
  isEUCitizen: false,
  mainSurveyCompleteDateISO: "2021-09-06T16:27:21.855Z",
  surveyNameSpace: "personaSurveyUS",
  personaSurveyUS: {
    basicHealthGoal: ["fit"],
    heightFeet: 5,
    heightInch: 5,
    weight: 200,
    heightCm: 165.1,
    weightKg: 90.70294784580499,
    currentBMI: 33.27810650887574,
    idealWeight: 170,
    idealWeightKg: 77.09750566893423,
    targetBMI: 22.4,
    sex: ["female"],
    gender: ["female"],
    pregnant: ["no"],
    ageRange: ["30s"],
    email,
    ...payloadOverrides,
  },
  email,
  latestMixPanelAlias: email,
  language: "en",
  country: "US",
  subdivision: "NY",
  postalCode: "11375",
  lastUpdated: 1630945641855,
});

const getPayloadForEnrollUser = (
  email: string,
  nuid: string,
  password: string,
  payloadOverrides?: Record<string, any>
) => ({
  userProfileData: {
    email,
    name: "Mike Jones",
    password,
    phoneNumber: "8053308004",
    language: "en",
    gender: "FEMALE",
    heightInCm: 167.64000000000001,
    weightInKg: 70.29478458049887,
    age: 30,
    ...payloadOverrides,
  },
  action: "create_account",
  name: "Mike Jones",
  email,
  password,
  phone_number: "8053308004",
  is_eu_citizen: false,
  gdpr_consent:
    '{"granted": true, "source": "AUTO", "time": "2021-09-07T09:57:55.967Z[Europe/Bucharest]"}',
  country_code: "US",
  language: "en",
  user_id: nuid,
  experiments: [],
  device_is_mobile: false,
});

const getGrowthProgramDates = (plan: Plan) => {
  const growthProgramStartDateISO = toISO();
  const expirationDate = shift_days(plan.trial_duration, new Date());
  const growthTrialEndDate = expirationDate.toLocaleDateString(getLocale());
  const growthTrialEndDateISO = toISO(expirationDate);
  return {
    growthProgramStartDateISO,
    growthTrialEndDate,
    growthTrialEndDateISO,
  };
};

const getPayloadForPurchaseRequest = (
  email: string,
  plan: Plan,
  purchaseOverrides?: Record<string, any>
) => ({
  name: "Mike Jones",
  email,
  countryCode: getCountryCode(),
  continentCode: "NA",
  noomPlanId: plan.noom_plan_id,
  braintreeId: plan.braintree_id,
  merchantAccount: plan.merchant_account,
  language: "en",
  curriculum: plan.curriculum ?? "HW",
  inAppPurchase: false,
  paymentMethodNonce: "pm_card_bypassPending",
  paymentMethodType: "CreditCard",
  plan,
  paymentProcessor: "STRIPE",
  zipcode: "10001",
  recaptchaToken: "dummy_recaptcha_token_not_needed",
  billingAddress: {
    country: "US",
    city: "NY",
    region: "New York",
    address1: "123 peace street",
    address2: "",
    zipcode: "10001",
    name: "Mike Jones",
  },
  one_source_tax_code: "S001",
  shouldEnroll: true,
  experiments: [],
  trialFee: plan.trial_item_options[0]?.price,
  trialSaleItemId: plan.trial_item_options[0]?.trial_sale_item_id,
  subscriptionSaleItemId: plan.sale_item_id,
  ...getGrowthProgramDates(plan),
  ...purchaseOverrides,
});

const getPayloadForPurchaseNoomPremium = (
  email: string,
  user_id: string,
  noomPremiumId: string
) => ({
  email,
  currencySymbol: "$",
  language: "en",
  shouldValidateAccessToken: false,
  user_id,
  productIds: [noomPremiumId],
  alwaysUseInclusiveTaxes: false,
});

export const createUserInGrowthDB = (
  email: string,
  payloadForCreateUserOverrides?: Record<string, any>
) => {
  const payload = getPayloadForCreateUser(email, payloadForCreateUserOverrides);
  return send("POST", "/userdata/api/v4/user/data/saveMainSurvey/", payload);
};

export const purchaseNoomPremium = (
  email: string,
  user_id: string,
  noomPremiumId: string
) => {
  const payload = getPayloadForPurchaseNoomPremium(
    email,
    user_id,
    noomPremiumId
  );
  return send("POST", "/api/payment/v2/purchase_add_on/", payload);
};

export type PayloadForPurchaseOverrides = {
  purchaseOverrides?: Record<string, any>;
  createUserOverrides?: Record<string, any>;
  enrollUserOverrides?: Record<string, any>;
};

export const makePurchase = (
  email: string,
  password: string,
  plan: Plan,
  updateStatus: (string) => void,
  payloadOverrides?: PayloadForPurchaseOverrides
) => {
  const createUser = () => {
    updateStatus("Creating user...");
    return createUserInGrowthDB(email, payloadOverrides?.createUserOverrides);
  };

  const enrollUser = (createUserResponse) => {
    updateStatus("Enrolling user...");
    const payload = getPayloadForEnrollUser(
      email,
      createUserResponse.user_id,
      password,
      payloadOverrides?.enrollUserOverrides
    );
    return send("POST", "/api/enrollment/v2/create_account/", payload);
  };

  const authenticate = () => {
    updateStatus("Authenticating user...");
    return authenticateUser(email, password);
  };

  const sendPurchaseRequest = () => {
    updateStatus("Purchasing plan...");
    const payload = getPayloadForPurchaseRequest(
      email,
      plan,
      payloadOverrides?.purchaseOverrides
    );
    return send("POST", "/api/payment/v2/purchase_program/", payload);
  };

  return createUser()
    .then(enrollUser)
    .then(authenticate)
    .then(sendPurchaseRequest)
    .catch((err) => {
      console.log(err);
      console.log(err.response);
      throw err;
    });
};

export type BodyGoalType = NonNullable<
  SurveyAnswersState["bodyCompositionGoal"]
>[0];

const HEALTH_CHECK_PLAN_CONFIGS: Partial<
  Record<HealthPlan, Record<string, unknown>>
> = {
  [MED_SKU.COMPOUNDED]: {
    ...HIGH_BMI_PROFILE,
    ...PD_QUALIFIED,
    ...HAS_PRIOR_MEDS,
  },
  [MED_SKU.GENERIC_LIRAGLUTIDE]: {
    ...HIGH_BMI_PROFILE,
    ...PD_NOT_QUALIFIED,
    ...HAS_PRIOR_MEDS,
  },
  [MED_SKU.BRANDED]: {
    ...HIGH_BMI_PROFILE,
    ...PD_NOT_QUALIFIED,
    ...HAS_PRIOR_MEDS,
  },
  [MED_SKU.ORALS_METFORMIN]: {
    ...HIGH_BMI_PROFILE,
    ...NO_PRIOR_MEDS,
  },
  [MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER]: {
    ...LOW_BMI_PROFILE,
    ...NO_PRIOR_MEDS,
  },
  [NON_MED_PLAN.NOOM_WEIGHT]: {
    ...HIGH_BMI_PROFILE,
    ...NO_PRIOR_MEDS,
    currentHealthRiskMedical: ["pancreatitis"],
  },
  [NON_MED_PLAN.FREEMIUM]: {
    ...HIGH_BMI_PROFILE,
    ...NO_PRIOR_MEDS,
    idealWeight: 145,
    idealWeightKg: 65.77,
    currentHealthRiskMedical: ["pancreatitis"],
  },
};

export const getPayloadForHealthCheckUser = (
  email: string,
  planType: HealthPlan,
  bodyGoal: BodyGoalType
): {
  surveyAnswers: SaveSurveyAnswerParams[0];
  params: {
    surveyNameSpace: "personaSurveyUS";
    dateKey?: string;
    extraProps?: Record<string, unknown>;
  };
} => {
  const baseAnswers = {
    goal:
      bodyGoal === "loseWeight"
        ? ["weightMetabolism", "energy"]
        : ["energy", "focusBrainHealth"],
    bodyCompositionGoal: [bodyGoal],
    firstName: "Test",
    sex: [sexConstants.female],
    gender: [genderConstants.female],
    pregnant: ["no"],
    ageRange: ["30s"],
    zipcode: "10001",
    email,
    wakeUpEnergy: ["rarely"],
    energyLevel: ["oftenLow"],
    stressFrequency: ["someDays"],
    focus: ["someDays"],
    winded: ["rarely"],
    fitnessChanges: ["none"],
    inflammationDiscomfort: ["mostDays"],
    appetite: ["changesDayToDay"],
    cravings: ["someDays"],
    proteinMeals: ["mostMeals"],
    processedFoods: ["someDays"],
    sugaryFoodDrinks: ["someDays"],
    sleepHours: ["7to8"],
    alcohol: ["rarely"],
    smoking: ["no"],
    destress: ["regularlyPractice"],
    cardio: ["3to4"],
    strength: ["1to2"],
    steps: ["5000to7500"],
    brainFamilyHistory: ["none"],
    inflammationHistory: ["none"],
    heartFamilyHistory: ["none"],
    heartDiagnoses: ["none"],
    metabolicDiagnoses: ["none"],
    a1c: ["no"],
    currentHealthRiskMedical: ["none"],
    eatingDisorder: ["no"],
    medicare: ["no"],
  };

  const planSpecificAnswers = HEALTH_CHECK_PLAN_CONFIGS[planType] ?? {
    ...HIGH_BMI_PROFILE,
    ...HAS_PRIOR_MEDS,
  };

  return {
    surveyAnswers: {
      ...baseAnswers,
      ...planSpecificAnswers,
    } as SaveSurveyAnswerParams[0],
    params: {
      surveyNameSpace: "personaSurveyUS" as const,
      extraProps: {
        name: "",
        clientTimestamp: "2021-09-06T19:27:21.855Z[Europe/Bucharest]",
        timezone: "Europe/Bucharest",
        isEUCitizen: false,
        mainSurveyCompleteDateISO: "2021-09-06T16:27:21.855Z",
        email,
        latestMixPanelAlias: email,
        language: "en",
        lastUpdated: 1630945641855,
      },
    },
  };
};
